/**
 * ListiniFornitoriPage — Console operatore /listini-fornitori (atlante v2, cap. 435, 28/10/2026).
 *
 * Il listino di ogni fornitore che ne ha uno a versioni: la versione in vigore, da dove arriva (la fonte che il giro del
 * mattino controlla), l'ultimo controllo, la BOZZA che il giro ha preparato quando il file è cambiato, e le versioni già
 * pubblicate. Da qui si controlla la fonte subito e si pubblica la bozza, scegliendo se avvisare gli amministratori delle
 * aziende clienti (spento finché chi pubblica non lo accende: Simone, 28/10/2026, «non voglio far partire email a
 * nessuno»). Nessun preventivo cambia: lo sceglie ogni amministratore (cap. 432).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Link, Paper, Snackbar, Stack, Typography } from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme';
import {
  controllaLaFonte,
  leggiListiniDeiFornitori,
  motivoErrore,
  pubblicaLaVersione,
  type EsitoDelControllo,
  type ListinoDelFornitore,
} from '../services/listiniFornitoriService';
import { BozzaDelListino } from '../components/listini/BozzaDelListino';
import { ConfermaPubblicazione } from '../components/listini/ConfermaPubblicazione';
import { StoriaDelListino } from '../components/listini/StoriaDelListino';
import { useFormatiListino } from '../components/listini/formati';

type Avviso = { gravita: 'success' | 'info' | 'warning' | 'error'; testo: string };

const Riga: React.FC<{ etichetta: string; children: React.ReactNode }> = ({ etichetta, children }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ width: { sm: 150 }, flexShrink: 0 }}>{etichetta}</Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Stack>
);

const PannelloDelFornitore: React.FC<{
  listino: ListinoDelFornitore;
  onControllato: (esito: EsitoDelControllo) => void;
  onPubblica: (avvisa: boolean) => void;
}> = ({ listino, onControllato, onPubblica }) => {
  const { t } = useTranslation('listini');
  const f = useFormatiListino();
  const [controllando, setControllando] = useState(false);
  const inVigore = listino.versioni.find((v) => v.numero === listino.ultima_pubblicata);
  const controlla = async () => {
    setControllando(true);
    try { onControllato(await controllaLaFonte(listino.fornitore_id)); }
    catch (e) { onControllato({ esito: 'errore', fornitore: listino.fornitore_id, motivo: motivoErrore(e) }); }
    finally { setControllando(false); }
  };
  const problema = listino.problema === 'file_non_trovato' ? t('fornitore.file_non_trovato') : listino.problema;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" component="h2">{listino.nome}</Typography>
          <Typography variant="body1" sx={{ mt: 0.25 }}>
            {t('fornitore.in_vigore', { numero: listino.ultima_pubblicata })}
            {inVigore?.fonte.data_documento && inVigore.fonte.tipo !== 'catalogo' && (
              <Typography component="span" color="text.secondary">{`, ${t('fornitore.del_listino', { data: f.giorno(inVigore.fonte.data_documento) })}`}</Typography>
            )}
          </Typography>
        </Box>
        {listino.fonte && (
          <Button variant="outlined" onClick={controlla} disabled={controllando} sx={{ minHeight: 44, flexShrink: 0 }} startIcon={controllando ? <CircularProgress size={16} /> : <Refresh />}>
            {controllando ? t('fornitore.controllo_in_corso') : t('fornitore.controlla')}
          </Button>
        )}
      </Stack>

      <Stack spacing={1} sx={{ mb: 2.5 }}>
        <Riga etichetta={t('fornitore.fonte')}>
          {listino.fonte?.tipo === 'cartella_drive_pubblica' && listino.fonte.cartella_id ? (
            <Typography variant="body2">
              {t('fornitore.fonte_drive', { file: listino.fonte.nome_file ?? '' })}{' '}
              <Link href={`https://drive.google.com/drive/folders/${encodeURIComponent(listino.fonte.cartella_id)}`} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, whiteSpace: 'nowrap' }}>
                {t('fornitore.apri_cartella')} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
            </Typography>
          ) : <Typography variant="body2" color="text.secondary">{t('fornitore.senza_fonte')}</Typography>}
        </Riga>
        {listino.fonte && (
          <Riga etichetta={t('fornitore.ultimo_controllo')}>
            <Typography variant="body2">{listino.controllato_il ? f.momento(listino.controllato_il) : t('fornitore.mai_controllato')}</Typography>
          </Riga>
        )}
        {problema && <Alert severity="warning" variant="outlined">{t('fornitore.problema', { motivo: problema })}</Alert>}
      </Stack>

      {listino.bozza
        ? <BozzaDelListino bozza={listino.bozza} onPubblica={onPubblica} />
        : <Typography variant="body2" color="text.secondary" sx={{ borderTop: `1px solid ${tokens.color.line}`, py: 2 }}>{t('bozza.nessuna')}</Typography>}

      <Box sx={{ mt: listino.bozza ? 3 : 0 }}>
        <StoriaDelListino versioni={listino.versioni} />
      </Box>
    </Paper>
  );
};

export const ListiniFornitoriPage: React.FC = () => {
  const { t } = useTranslation('listini');
  const [listini, setListini] = useState<ListinoDelFornitore[]>([]);
  const [caricando, setCaricando] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [daConfermare, setDaConfermare] = useState<{ listino: ListinoDelFornitore; avvisa: boolean } | null>(null);
  const [pubblicando, setPubblicando] = useState(false);
  const [avviso, setAvviso] = useState<Avviso | null>(null);

  const carica = useCallback(async () => {
    setCaricando(true);
    setErrore(null);
    try { setListini(await leggiListiniDeiFornitori()); }
    catch (e) { console.error('[ListiniFornitoriPage] lettura non riuscita', e); setErrore(motivoErrore(e)); }
    finally { setCaricando(false); }
  }, []);
  useEffect(() => { void carica(); }, [carica]);

  const dopoIlControllo = (esito: EsitoDelControllo) => {
    if (esito.esito === 'errore') setAvviso({ gravita: 'error', testo: t('esiti_controllo.errore', { motivo: esito.motivo }) });
    else if (esito.esito === 'bozza') setAvviso({ gravita: 'success', testo: t('esiti_controllo.bozza', { numero: esito.versione }) });
    else setAvviso({ gravita: esito.esito === 'invariato' ? 'info' : 'warning', testo: t(`esiti_controllo.${esito.esito}`) });
    void carica();
  };

  const pubblica = async () => {
    if (!daConfermare?.listino.bozza) return;
    const { listino, avvisa } = daConfermare;
    setPubblicando(true);
    try {
      const esito = await pubblicaLaVersione(listino.fornitore_id, listino.bozza!.numero, avvisa);
      setAvviso({ gravita: 'success', testo: t('pubblica.fatto', { numero: esito.versione, prezzi: esito.prodotti_aggiornati, escono: esito.spariti }) });
      setDaConfermare(null);
      await carica();
    } catch (e) {
      setAvviso({ gravita: 'error', testo: t('pubblica.errore', { motivo: motivoErrore(e) }) });
    } finally {
      setPubblicando(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ maxWidth: 720 }}>
          <Typography variant="h4" component="h1">{t('pagina.titolo')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{t('pagina.descrizione')}</Typography>
        </Box>
        <Button variant="text" startIcon={<Refresh />} onClick={() => void carica()} disabled={caricando} sx={{ minHeight: 40, flexShrink: 0 }}>{t('pagina.ricarica')}</Button>
      </Stack>

      {errore && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => void carica()}>{t('pagina.ricarica')}</Button>}>{t('pagina.errore', { motivo: errore })}</Alert>}
      {caricando && !listini.length ? (
        <Stack direction="row" spacing={1.5} alignItems="center"><CircularProgress size={20} /><Typography variant="body2">{t('pagina.caricamento')}</Typography></Stack>
      ) : !listini.length && !errore ? (
        <Alert severity="info">{t('pagina.nessuno')}</Alert>
      ) : (
        <Stack spacing={3}>
          {listini.map((l) => (
            <PannelloDelFornitore key={l.fornitore_id} listino={l} onControllato={dopoIlControllo} onPubblica={(avvisa) => setDaConfermare({ listino: l, avvisa })} />
          ))}
        </Stack>
      )}

      {daConfermare?.listino.bozza && (
        <ConfermaPubblicazione fornitore={daConfermare.listino.nome} bozza={daConfermare.listino.bozza} avvisa={daConfermare.avvisa} lavorando={pubblicando} onAnnulla={() => setDaConfermare(null)} onConferma={() => void pubblica()} />
      )}
      <Snackbar open={!!avviso} autoHideDuration={6000} onClose={() => setAvviso(null)}>
        {avviso ? <Alert severity={avviso.gravita} variant="filled" onClose={() => setAvviso(null)}>{avviso.testo}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default ListiniFornitoriPage;
